import React, { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import useCustomerContext from "Contexts/customer-context";
import { useGlobalNotificationBanner } from "Shared/util/custom-hooks";
import { CustomerStatus } from "Shared/util/types";
import { toggleImbox } from "Shared/util/imbox-api";
import TextLink from "Shared/components/text-link";

export const useGlobalCompanyStatusNotificationBanners = () => {
    const [
        showOnboardingMissingInformation,
        setShowOnboardingMissingInformation,
    ] = useState(false);
    const [showOnboardingPendingApproval, setShowOnboardingPendingApproval] =
        useState(false);
    const [showMissingInformation, setShowMissingInformation] = useState(false);
    const [showPendingApproval, setShowPendingApproval] = useState(false);
    const history = useHistory();
    const location = useLocation();
    const { customerData } = useCustomerContext();
    const {
        NotificationBannerType,
        addGlobalNotificationBanner,
        removeGlobalNotificationBanner,
    } = useGlobalNotificationBanner();

    const missingOnboardingCustomerInformationId =
        "MISSING_ONBOARDING_CUSTOMER_INFORMATION";
    const pendingOnboardingCustomerApprovalId =
        "PENDING_ONBOARDING_CUSTOMER_APPROVAL";
    const missingCustomerInformationId = "MISSING_CUSTOMER_INFORMATION";
    const pendingCustomerApprovalId = "PENDING_CUSTOMER_APPROVAL";

    useEffect(() => {
        if (
            !customerData?.status ||
            customerData.status.status === CustomerStatus.COMPLETE_AND_ACTIVE ||
            customerData.status.status === CustomerStatus.INACTIVE ||
            customerData.status.status === CustomerStatus.UNKNOWN
        ) {
            removeGlobalNotificationBanner(
                missingOnboardingCustomerInformationId,
            );
            removeGlobalNotificationBanner(pendingOnboardingCustomerApprovalId);
            removeGlobalNotificationBanner(missingCustomerInformationId);
            removeGlobalNotificationBanner(pendingCustomerApprovalId);

            setShowOnboardingMissingInformation(false);
            setShowOnboardingPendingApproval(false);
            setShowMissingInformation(false);
            setShowPendingApproval(false);
            return;
        }

        const {
            status,
            signatoriesCompleted,
            paymentInformationCompleted: payoutInformationCompleted,
            kycCompleted,
            kycSupported,
        } = customerData.status;

        const missingInformation =
            !signatoriesCompleted ||
            !payoutInformationCompleted ||
            (kycSupported && !kycCompleted);
        const pendingApproval =
            signatoriesCompleted &&
            payoutInformationCompleted &&
            (!kycSupported || kycCompleted);

        setShowOnboardingMissingInformation(
            status === CustomerStatus.ONBOARDING_IN_PROGRESS &&
                missingInformation,
        );
        setShowOnboardingPendingApproval(
            status === CustomerStatus.ONBOARDING_IN_PROGRESS && pendingApproval,
        );
        setShowMissingInformation(
            status === CustomerStatus.PENDING_MODIFICATIONS &&
                missingInformation,
        );
        setShowPendingApproval(
            status === CustomerStatus.PENDING_MODIFICATIONS && pendingApproval,
        );
    }, [customerData?.status]);

    useEffect(() => {
        if (
            !showOnboardingMissingInformation &&
            !showOnboardingPendingApproval &&
            !showMissingInformation &&
            !showPendingApproval
        )
            return;

        if (!showOnboardingMissingInformation)
            removeGlobalNotificationBanner(
                missingOnboardingCustomerInformationId,
            );
        if (!showOnboardingPendingApproval)
            removeGlobalNotificationBanner(pendingOnboardingCustomerApprovalId);
        if (!showMissingInformation)
            removeGlobalNotificationBanner(missingCustomerInformationId);
        if (!showPendingApproval)
            removeGlobalNotificationBanner(pendingCustomerApprovalId);

        const isCompanySettingsPage =
            location.pathname === "/company-settings" ||
            location.pathname === "/company-settings/payout-information";

        if (showOnboardingMissingInformation) {
            if (isCompanySettingsPage) {
                addGlobalNotificationBanner(
                    (tnb) => tnb.onboarding_missing_information,
                    NotificationBannerType.Attention,
                    false,
                    missingOnboardingCustomerInformationId,
                );
            } else {
                const content = (tnb: any) => (
                    <>
                        {tnb.onboarding_missing_information}{" "}
                        <TextLink
                            text={tnb.missing_information_link}
                            link={() => history.push("/company-settings")}
                        />
                    </>
                );
                addGlobalNotificationBanner(
                    content,
                    NotificationBannerType.Attention,
                    false,
                    missingOnboardingCustomerInformationId,
                );
            }
        } else if (showOnboardingPendingApproval) {
            const content = (tnb: any) => (
                <>
                    {tnb.onboarding_pending_approval}{" "}
                    <TextLink
                        text={tnb.onboarding_pending_approval_link}
                        link={toggleImbox}
                    />
                </>
            );
            addGlobalNotificationBanner(
                content,
                NotificationBannerType.Information,
                false,
                pendingOnboardingCustomerApprovalId,
            );
        } else if (showMissingInformation) {
            if (isCompanySettingsPage) {
                addGlobalNotificationBanner(
                    (tnb) => tnb.missing_information,
                    NotificationBannerType.Attention,
                    false,
                    missingCustomerInformationId,
                );
            } else {
                const content = (tnb: any) => (
                    <>
                        {tnb.missing_information}{" "}
                        <TextLink
                            text={tnb.missing_information_link}
                            link={() => history.push("/company-settings")}
                        />
                    </>
                );
                addGlobalNotificationBanner(
                    content,
                    NotificationBannerType.Attention,
                    false,
                    missingCustomerInformationId,
                );
            }
        } else if (showPendingApproval) {
            const content = (tnb: any) => (
                <>
                    {tnb.pending_approval}{" "}
                    <TextLink
                        text={tnb.pending_approval_link}
                        link={toggleImbox}
                    />
                </>
            );
            addGlobalNotificationBanner(
                content,
                NotificationBannerType.Information,
                false,
                pendingCustomerApprovalId,
            );
        }
    }, [
        showOnboardingMissingInformation,
        showOnboardingPendingApproval,
        showMissingInformation,
        showPendingApproval,
        location.pathname,
    ]);
};
